import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
	AgentPreview,
	InviteUserPayload,
	ProspectivePayload,
	User,
	UserGrantsResponse,
	UserPreferences,
	UserPreview,
	UserProfile,
	UserRole
} from '../../types/user';
import { API, Auth } from 'aws-amplify';
import { getCachedAuthUser } from '../../utils/cachedAuth';
import { AppDispatch, RootState } from '../store';
import { RequestStatusObject, buildReducers, createLazyList, createRequestStatusObject } from '../utils';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';
import { LazyList, LazyListRequest, UserLazyList, UserLazyListRequest } from '../../types/types';
import { StripeCheckoutResponse } from '../../types/products';
import { UserExportColumn } from '../../constants/userExportColumns';

const initialState: {
	currentUser: User | undefined;
	preferences: UserPreferences | undefined;
	walletStorage: any; // TODO - define type
	pageUser: User | undefined;
	list: UserLazyList;
	usersOrganisations: any; // TODO - define type
	previews: { agent?: AgentPreview[] } & { [roleName: string]: UserPreview[] | undefined };
	userGrantsList: UserGrantsResponse | null;
	userOnboardingLicense: StripeCheckoutResponse | undefined;
	usersActiveSubscriptions: LazyList<any>; // TODO - define type
	usersUnlicensedRequests: LazyList<any>; // TODO - define type
	userFreeTrial: any;
	userInfiniteFreeTier: any;
	userGracePeriod: any;
	stripeVerification: any; // TODO - define type
	verifyingUser: User | undefined;
	readUserRequest: RequestStatusObject;
	readPreferencesRequest: RequestStatusObject;
	readPageUserRequest: RequestStatusObject;
	readVerifyingUserRequest: RequestStatusObject;
	readUserListRequest: RequestStatusObject;
	updateUserRequest: RequestStatusObject;
	incrementUserTotpSkipCountRequest: RequestStatusObject;
	setUserMFARequest: RequestStatusObject;
	updateUserLastLoginRequest: RequestStatusObject;
	createUserRequest: RequestStatusObject;
	createProspectiveRequest: RequestStatusObject;
	isLoading?: boolean;
	walletStorageRequest: RequestStatusObject;
	usersOrganisationsRequest: RequestStatusObject;
	usersGrantsRequest: RequestStatusObject;
	userLeavingOrganisationRequest: RequestStatusObject;
	userOnboardingLicenseRequest: RequestStatusObject;
	totpLostRequest: RequestStatusObject;
	usersActiveSubscriptionsRequest: RequestStatusObject;
	cancelActiveSubscriptionRequest: RequestStatusObject;
	usersUnlicensedStatusRequests: RequestStatusObject;
	userFreeTrialRequest: RequestStatusObject;
	userInfiniteFreeTierRequest: RequestStatusObject;
	userGracePeriodRequest: RequestStatusObject;
	stripeVerificationRequest: RequestStatusObject;
} = {
	currentUser: undefined,
	preferences: undefined,
	walletStorage: undefined,
	pageUser: undefined,
	usersOrganisations: undefined,
	userGrantsList: null,
	userOnboardingLicense: undefined,
	usersActiveSubscriptions: createLazyList(),
	usersUnlicensedRequests: createLazyList(),
	userFreeTrial: undefined,
	userInfiniteFreeTier: undefined,
	userGracePeriod: undefined,
	list: {
		roleName: undefined,
		...createLazyList()
	},
	stripeVerification: undefined,
	verifyingUser: undefined,
	previews: {},
	readUserRequest: createRequestStatusObject(),
	readPreferencesRequest: createRequestStatusObject(),
	readPageUserRequest: createRequestStatusObject(),
	readVerifyingUserRequest: createRequestStatusObject(),
	readUserListRequest: createRequestStatusObject(),
	updateUserRequest: createRequestStatusObject(),
	incrementUserTotpSkipCountRequest: createRequestStatusObject(),
	setUserMFARequest: createRequestStatusObject(),
	updateUserLastLoginRequest: createRequestStatusObject(),
	createUserRequest: createRequestStatusObject(),
	createProspectiveRequest: createRequestStatusObject(),
	walletStorageRequest: createRequestStatusObject(),
	usersOrganisationsRequest: createRequestStatusObject(),
	usersGrantsRequest: createRequestStatusObject(),
	userLeavingOrganisationRequest: createRequestStatusObject(),
	userOnboardingLicenseRequest: createRequestStatusObject(),
	totpLostRequest: createRequestStatusObject(),
	usersActiveSubscriptionsRequest: createRequestStatusObject(),
	cancelActiveSubscriptionRequest: createRequestStatusObject(),
	usersUnlicensedStatusRequests: createRequestStatusObject(),
	userFreeTrialRequest: createRequestStatusObject(),
	userInfiniteFreeTierRequest: createRequestStatusObject(),
	userGracePeriodRequest: createRequestStatusObject(),
	stripeVerificationRequest: createRequestStatusObject(),
	isLoading: false
};

export const fetchUserStorage = createAsyncThunk<void, undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/fetchUserStorage',

	async () => {
		try {
			const result = await API.get('inspirusapi', '/users/storage', {
				headers: {},
				response: true,
				queryStringParameters: {}
			});
			return result.data;
		} catch (error) {
			console.error('Error fetching user:', error);
		}
	}
);

export const listUsers = createAsyncThunk<
	UserLazyList,
	UserLazyListRequest & { search?: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/listUsers',

	async ({ next, limit, search }) => {
		const result = await API.get('inspirusapi', '/admins/users', {
			headers: {},
			response: true,
			queryStringParameters: { next, limit, search }
		});
		const finalResult = result.data;
		return finalResult;
	}
);

export interface ExportProgress {
	status: string;
	processedCount?: number;
	totalCount?: number;
	error?: string;
	// Set while account statuses are listed, before any users are read.
	accountStatusStage?: string;
	accountsListed?: number;
}

/**
 * How an export the page started ended: downloaded, or still running when the page stopped
 * waiting (it carries on, and its file is in Export History), or abandoned by the page.
 */
export type ExportOutcome = 'downloaded' | 'stillRunning' | 'aborted';

export const exportUsersCsv = createAsyncThunk<
	ExportOutcome,
	{ onProgress?: (progress: ExportProgress) => void; columns?: UserExportColumn[] },
	{ dispatch: AppDispatch }
>('user/exportUsersCsv', async ({ onProgress, columns }, { signal }) => {
	// Trigger async export
	let triggerResponse;
	try {
		triggerResponse = await API.post('inspirusapi', '/admins/users/export2', {
			headers: {},
			response: true,
			body: columns ? { columns } : {}
		});
	} catch (err: unknown) {
		const apiErr = err as { response?: { status?: number; data?: { error?: string; jobId?: string } } };
		if (apiErr.response?.status === 409) {
			throw new Error(apiErr.response.data?.error || 'An export is already in progress');
		}
		throw err;
	}
	const { jobId } = triggerResponse.data;

	onProgress?.({ status: 'pending' });

	// Poll for completion
	const maxAttempts = 120; // 10 minutes at 5s intervals
	const pollInterval = 5000;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		// Stop polling if the thunk was aborted (e.g. component unmount)
		if (signal.aborted) return 'aborted';

		await new Promise((resolve) => setTimeout(resolve, pollInterval));

		if (signal.aborted) return 'aborted';

		const statusResponse = await API.get('inspirusapi', `/admins/users/export2/status/${jobId}`, {
			headers: {},
			response: true
		});
		const job = statusResponse.data;

		onProgress?.({
			status: job.status,
			processedCount: job.processedCount,
			totalCount: job.totalCount,
			accountStatusStage: job.accountStatusStage,
			accountsListed: job.accountsListed
		});

		if (job.status === 'complete' && job.downloadUrl) {
			// Download from the pre-signed S3 URL
			const link = document.createElement('a');
			link.href = job.downloadUrl;
			link.download = 'users_export.csv';
			document.body.appendChild(link);
			link.click();
			link.remove();
			return 'downloaded';
		}

		if (job.status === 'failed') {
			throw new Error(job.error || 'Export failed');
		}
	}

	// The page has stopped waiting, not the export: a large one runs on and its file appears in
	// Export History, so this is not reported as a failure.
	return 'stillRunning';
});

export interface ExportJob {
	jobId: string;
	status: string;
	requestedBy: string;
	createdAt: string;
	updatedAt: string;
	userCount?: number;
	processedCount?: number;
	totalCount?: number;
	error?: string;
	downloadUrl?: string;
	accountStatusStage?: string;
	accountsListed?: number;
	columns?: UserExportColumn[];
}

export const fetchExportHistory = createAsyncThunk<ExportJob[], void, { dispatch: AppDispatch }>(
	'user/fetchExportHistory',
	async () => {
		const response = await API.get('inspirusapi', '/admins/users/export2/history', {
			headers: {},
			response: true
		});
		return response.data.jobs;
	}
);

export const deleteExport = createAsyncThunk<string, string, { dispatch: AppDispatch }>(
	'user/deleteExport',
	async (jobId) => {
		await API.del('inspirusapi', `/admins/users/export2/${jobId}`, {
			headers: {}
		});
		return jobId;
	}
);

export const retryExport = createAsyncThunk<{ jobId: string; status: string }, string, { dispatch: AppDispatch }>(
	'user/retryExport',
	async (jobId) => {
		const response = await API.post('inspirusapi', `/admins/users/export2/retry/${jobId}`, {
			headers: {},
			response: true,
			body: {}
		});
		return response.data;
	}
);

export const cancelExport = createAsyncThunk<{ jobId: string; status: string }, string, { dispatch: AppDispatch }>(
	'user/cancelExport',
	async (jobId) => {
		const response = await API.post('inspirusapi', `/admins/users/export2/cancel/${jobId}`, {
			headers: {},
			response: true,
			body: {}
		});
		return response.data;
	}
);

export const readCurrentUser = createAsyncThunk<User, undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/readCurrent',

	async () => {
		// current user Id
		const u = await getCachedAuthUser();

		const result = await API.get('inspirusapi', `/users/${u.attributes.sub}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		const user: User = result.data;
		// Fetching organisations for users
		const organisationIds = new Set();
		user.roles.forEach(({ organisationId }) => organisationIds.add(organisationId));
		const organisations = await Promise.all(
			Array.from(organisationIds).map(async (organisationId) =>
				API.get('inspirusapi', `/organisations/${organisationId}`, {
					headers: {},
					response: true,
					queryStringParameters: {}
				})
			)
		);
		user.organisations = organisations.map(({ data }) => ({
			...data,
			isPearsana: data.id === process.env.REACT_APP_PEARSANA_ID
		}));

		return user;
	}
);

export const readPreferences = createAsyncThunk<
	UserPreferences,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/readPreferences',

	async () => {
		// current user Id
		const u = await getCachedAuthUser();

		const result = await API.get('inspirusapi', `/users/${u.attributes.sub}/preferences`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		return result.data;
	}
);

export const readUser = createAsyncThunk<
	User,
	{ id: string; verificationInfo?: boolean },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/readUser',

	async ({ id, verificationInfo }) => {
		const result = await API.get('inspirusapi', `/users/${id}`, {
			headers: {},
			response: true,
			queryStringParameters: { verificationInfo }
		});
		return result.data;
	}
);

export const readAgent = createAsyncThunk<User, string, { dispatch: AppDispatch; state: RootState }>(
	'user/readAgent',

	async (id: string) => {
		const result = await API.get('inspirusapi', `/users/${id}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});
		return result.data;
	}
);

export const readOrganisationUsers = createAsyncThunk<
	UserLazyList,
	{ organisationId: string; agentId?: string } & UserLazyListRequest & {
			requests?: boolean;
			organisationAdminView?: boolean;
		},
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/readOrganisationUsers',

	async ({ organisationId, agentId, roleName, limit, next, requests, organisationAdminView }) => {
		// current user Id
		const result = await API.get('inspirusapi', `/organisations/${organisationId}/users`, {
			headers: {},
			response: true,
			queryStringParameters: {
				roleName,
				agentId,
				limit,
				next,
				requests,
				organisationAdminView
			}
		});

		return { ...result.data, roleName };
	}
);

export const readOrganisationUnlicensedUsers = createAsyncThunk<
	UserLazyList,
	{ organisationId: string } & UserLazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/readOrganisationUnlicensedUsers',

	async ({ organisationId, roleName, limit, next }) => {
		// current user Id
		const result = await API.get('inspirusapi', `/organisations/${organisationId}/users/requests`, {
			headers: {},
			response: true,
			queryStringParameters: {
				roleName,
				limit,
				next
			}
		});

		return { ...result.data, roleName };
	}
);

export const readOrganisationUserPreviews = createAsyncThunk<
	{ roleName: string; previews: UserPreview[] },
	{ organisationId: string; roleName: string; agentId?: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/readOrganisationUsersPreviews',

	async ({ organisationId, roleName, agentId }) => {
		// current user Id
		const result = await API.get('inspirusapi', `/organisations/${organisationId}/users`, {
			headers: {},
			response: true,
			queryStringParameters: {
				roleName,
				previews: true,
				agentId
			}
		});

		return { roleName, previews: result.data };
	}
);

export const listAgents = createAsyncThunk<AgentPreview[], undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/listAgents',

	async () => {
		// current user Id
		const result = await API.get('inspirusapi', '/agents/', {
			headers: {},
			response: true
		});

		return result.data;
	}
);

export const updateUser = createAsyncThunk<UserProfile, UserProfile, { dispatch: AppDispatch; state: RootState }>(
	'user/update',

	async (payload) => {
		// current user Id
		const user = await Auth.currentAuthenticatedUser();

		await API.put('inspirusapi', `/users/${user.attributes.sub}`, {
			headers: {},
			response: true,
			body: {
				...payload,
				confirmed: true
			}
		});
		return { ...payload, confirmed: true };
	}
);

export const incrementUserTOTPCount = createAsyncThunk<any, string, { dispatch: AppDispatch; state: RootState }>(
	'user/incrementUserTOTPCount',

	async (userId) => {
		const result = await API.put('inspirusapi', `/users/incrementTOTPCount/${userId}`, {
			headers: {},
			response: true,
			body: {}
		});
		return result.data;
	}
);

export const setUserMFA = createAsyncThunk<
	any,
	{ id: string; userMFA: boolean },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/setUserMFA',

	async ({ id, userMFA }) => {
		const result = await API.put('inspirusapi', '/users/mfa/setUserMFA', {
			headers: {},
			response: true,
			body: {
				id: id,
				userMFA: userMFA
			}
		});
		return result.data;
	}
);

export const updateUserLastLogin = createAsyncThunk<
	any,
	{ id: string; lastLoggedIn: string },
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/updateUserLastLogin',

	async ({ id, lastLoggedIn }) => {
		const result = await API.put('inspirusapi', '/users/lastLoggedIn/update', {
			headers: {},
			response: true,
			body: {
				id: id,
				lastLoggedIn: lastLoggedIn
			}
		});
		return result.data;
	}
);

export const userNotifyTOTPLockedOut = createAsyncThunk<
	void,
	{ cognitoId: string },
	{ dispatch: AppDispatch; state: RootState; rejectValue: any }
>(
	'lostTOTP/notifyTOTP',

	async (payload, { rejectWithValue }) => {
		try {
			const result = await API.post('inspirusapi', '/lost-totp', {
				headers: {},
				response: true,
				queryStringParameters: {},
				body: payload
			});
			if (result.status === 200) {
				enqueueSnackbar(result.data, { variant: 'success' });
			}
			return result.data;
		} catch (e: any) {
			return rejectWithValue(e?.response?.data?.message);
		}
	}
);

export const userRecoverTOTP = createAsyncThunk<
	void,
	string,
	{ dispatch: AppDispatch; state: RootState; rejectValue: any }
>(
	'lostTOTP/recoverTOTP',

	async (token, { rejectWithValue }) => {
		try {
			await API.post('inspirusapi', '/lost-totp/recover', {
				headers: {
					Authorization: `Bearer ${token}`
				},
				response: true,
				queryStringParameters: {},
				body: {}
			});
		} catch (e: any) {
			return rejectWithValue(e?.response?.data?.message);
		}
	}
);

/**
 * Sends a new invitation to an account that has never been activated, from the sign-in page's
 * "Forgot password" step (PTEC-1046). Rejects with the HTTP status, so the page can tell a
 * rate limit (429) from any other failure. The response never says whether an invitation went
 * out, so there is nothing to return.
 */
export const userResendInvitation = createAsyncThunk<
	void,
	{ email: string },
	{ dispatch: AppDispatch; state: RootState; rejectValue: number | undefined }
>('lostTOTP/resendInvitation', async ({ email }, { rejectWithValue }) => {
	try {
		await API.post('inspirusapi', '/lost-totp/resend-invite', {
			headers: {},
			body: { email }
		});
	} catch (e: any) {
		return rejectWithValue(e?.response?.status);
	}
});

export const updateUserRoles = createAsyncThunk<
	void,
	{ userId: string; rolesToAdd: UserRole[]; rolesToDelete: UserRole[]; current?: boolean },
	{ dispatch: AppDispatch; state: RootState }
>('user/update/roles', async ({ userId, rolesToAdd, rolesToDelete, current }, { dispatch }) => {
	if (rolesToDelete.length) {
		await API.del('inspirusapi', `/users/${userId}/roles`, {
			headers: {},
			response: true,
			body: {
				roles: rolesToDelete
			}
		});
	}
	if (rolesToAdd.length) {
		await API.put('inspirusapi', `/users/${userId}/roles`, {
			response: true,
			body: {
				roles: rolesToAdd
			}
		});
	}
	if (!current) {
		dispatch(readUser({ id: userId }));
	} else {
		dispatch(readCurrentUser());
	}
});

export const deleteUser = createAsyncThunk<User, User | undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/deleteUser',

	async (profile) => {
		const result = await API.del('inspirusapi', '/users', {
			headers: {},
			response: true,
			queryStringParameters: {},
			body: { profile }
		});

		return result.data;
	}
);
export const inviteUser = createAsyncThunk<
	void,
	InviteUserPayload,
	{ dispatch: AppDispatch; state: RootState; rejectValue: any }
>(
	'user/inviteUser',

	async (payload, { rejectWithValue }) => {
		try {
			const result = await API.post('inspirusapi', '/users', {
				headers: {},
				response: true,
				queryStringParameters: {},
				body: payload
			});
			enqueueSnackbar(result.data.message, { variant: 'success' });
			return result.data;
		} catch (e: any) {
			return rejectWithValue(e?.response?.data?.message);
		}
	}
);

export const createProspectiveUser = createAsyncThunk<
	void,
	ProspectivePayload,
	{ dispatch: AppDispatch; state: RootState }
>(
	'user/createProspectiveUser',

	async (payload) => {
		const result = await API.post('inspirusapi', '/users/prospective', {
			headers: {},
			response: true,
			queryStringParameters: {},
			body: payload
		});

		enqueueSnackbar(result.data.message, { variant: 'success' });
	}
);

export const createInspirus = createAsyncThunk<void, undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/createInspirus',

	async () => {
		const path = '/inspirus/create-user/';
		const result = await API.post('inspirusapi', path, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});

		return result.data;
	}
);

export const usersOrganisations = createAsyncThunk<
	any,
	{
		userId: string;
	},
	{ dispatch: AppDispatch; state: RootState }
>('user/usersOrganisations', async ({ userId }) => {
	const result = await API.get('inspirusapi', `/users/${userId}/organisations`, {
		headers: {},
		response: true
	});

	return result.data;
});

export const userGrants = createAsyncThunk<
	any,
	{ userId?: string } | undefined,
	{ dispatch: AppDispatch; state: RootState }
>('user/userGrants', async (args) => {
	const queryStringParameters: Record<string, string> = {};
	if (args?.userId) {
		queryStringParameters.userId = args.userId;
	}
	const result = await API.get('inspirusapi', '/users/grants', {
		headers: {},
		response: true,
		queryStringParameters
	});

	return result.data;
});

export const userLeavingOrganisation = createAsyncThunk<
	void,
	{ userId: string; organisationId: string },
	{ dispatch: AppDispatch; state: RootState }
>('user/userLeavingOrganisation', async ({ userId, organisationId }) => {
	await API.del('inspirusapi', `/users/${userId}/roles/organisation/${organisationId}`, {
		headers: {},
		response: true
	});
});

export const userOnboardingLicense = createAsyncThunk<
	StripeCheckoutResponse,
	{ userId: string; licenseId: string; currency: string; coupon?: string; subscription?: string },
	{ dispatch: AppDispatch; state: RootState }
>('user/userOnboardingLicense', async ({ userId, licenseId, currency, coupon, subscription }) => {
	const result = await API.post('inspirusapi', `/products/${userId}`, {
		headers: {},
		response: true,
		body: { products: [licenseId] },
		queryStringParameters: {
			type: 'user',
			currency,
			coupon,
			subscription
		}
	});
	return result.data;
});

export const getActiveSubscriptions = createAsyncThunk<
	any,
	LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>('user/getActiveSubscriptions', async () => {
	const result = await API.get('inspirusapi', '/users/subscriptions', {
		headers: {},
		response: true,
		queryStringParameters: {}
	});
	return result.data;
});

export const cancelActiveSubscription = createAsyncThunk<
	void,
	{ subscriptionId: string },
	{ dispatch: AppDispatch; state: RootState }
>('user/cancelActiveSubscription', async ({ subscriptionId }) => {
	await API.del('inspirusapi', `/users/subscriptions/${subscriptionId}`, {
		headers: {},
		response: true
	});
});

export const userFreeTrial = createAsyncThunk<void, undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/userFreeTrial',
	async () => {
		const result = await API.post('inspirusapi', '/users/trial', {
			headers: {},
			response: true
		});
		return result.data;
	}
);

export const userInfiniteFreeTier = createAsyncThunk<void, undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/userInfiniteFreeTier',
	async () => {
		const result = await API.post('inspirusapi', '/users/infiniteFreeTier', {
			headers: {},
			response: true
		});
		return result.data;
	}
);

export const getUserGracePeriod = createAsyncThunk<any, undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/getUserGracePeriod',
	async () => {
		const result = await API.get('inspirusapi', '/users/grace-period', {
			headers: {},
			response: true
		});
		return result.data;
	}
);

export const stripeVerifyIdentity = createAsyncThunk<void, undefined, { dispatch: AppDispatch; state: RootState }>(
	'user/stripeVerifyIdentity',
	async () => {
		const result = await API.post('inspirusapi', '/users/verify-identity', {
			headers: {},
			response: true
		});
		return result.data;
	}
);

// Fetch a user for the verification info dialog without overwriting state.pageUser
export const readVerifyingUser = createAsyncThunk<
	User,
	{ id: string; verificationInfo?: boolean },
	{ dispatch: AppDispatch; state: RootState }
>('user/readVerifyingUser', async ({ id, verificationInfo }) => {
	const result = await API.get('inspirusapi', `/users/${id}`, {
		headers: {},
		response: true,
		queryStringParameters: { verificationInfo }
	});
	return result.data;
});

const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		resetUser: (state) => {
			state = initialState;
			return state;
		},
		updateUserStripeIdentityVerified: (state, { payload }) => {
			if (state.currentUser) {
				state.currentUser = {
					...state.currentUser,
					stripeIdentityVerified: payload
				};
			}
		},
		updateUsersMFAStatus: (state, action) => {
			const user = state.list.items.find((user) => user.id === action.payload);
			if (user) {
				user.userMfaSettings = 'N/A';
			}
		},
		resetUserConfirmedStatus: (state) => {
			if (state.currentUser) {
				state.currentUser = {
					...state.currentUser,
					confirmed: false
				};
			}
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, readCurrentUser, 'readUserRequest', {
			onFulfilled: (state, payload) => {
				state.currentUser = payload;
			}
		});
		buildReducers(builder, readPreferences, 'readPreferencesRequest', {
			onFulfilled: (state, payload) => {
				state.preferences = payload;
			}
		});
		buildReducers(builder, readUser, 'readPageUserRequest', {
			onFulfilled: (state, payload) => {
				state.pageUser = payload;
				state.isLoading = false;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar(i18n.t('common.types.user.readUserFailure'), { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, readOrganisationUsers, 'readUserListRequest', {
			onFulfilled: (state, payload) => {
				const list = state.list;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
				list.roleName = payload.roleName;
				state.isLoading = false;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar(i18n.t('common.types.user.readUserFailure'), { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, listUsers, 'readUserListRequest', {
			onFulfilled: (state, payload) => {
				const list = state.list;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
			}
		});
		buildReducers(builder, readOrganisationUserPreviews, 'readUserListRequest', {
			onFulfilled: (state, payload) => {
				state.previews[payload.roleName] = payload.previews;
			}
		});
		buildReducers(builder, listAgents, 'readUserListRequest', {
			onFulfilled: (state, payload) => {
				state.previews['agent'] = payload;
			}
		});
		buildReducers(builder, updateUser, 'updateUserRequest', {
			onFulfilled: (state, payload) => {
				state.currentUser = { ...state.currentUser, ...payload };
				enqueueSnackbar(i18n.t('common.types.user.profile.successMessage'), { variant: 'success' });
			},
			onRejected: (state, action, detailedErrorFromAPI) => {
				if (detailedErrorFromAPI) {
					enqueueSnackbar(detailedErrorFromAPI, { variant: 'error' });
				}
				enqueueSnackbar(i18n.t('common.types.user.profile.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, incrementUserTOTPCount, 'incrementUserTotpSkipCountRequest', {
			onFulfilled: (state, payload) => {
				state.currentUser = { ...state.currentUser, totpSkipCount: payload.Attributes.totpSkipCount };
				state.isLoading = false;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state, error) => {
				enqueueSnackbar(error.message, { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, setUserMFA, 'setUserMFARequest', {
			onFulfilled: (state, payload) => {
				state.currentUser = { ...state.currentUser, userMFASet: payload.Attributes.userMFASet };
			},
			onRejected: (state, action, detailedErrorFromAPI) => {
				if (detailedErrorFromAPI) {
					enqueueSnackbar(detailedErrorFromAPI, { variant: 'error' });
				}
			}
		});
		buildReducers(builder, updateUserLastLogin, 'updateUserLastLoginRequest', {
			// The daily heartbeat writes to DynamoDB and returns the fresh
			// loginStreakDays/loginCount (ReturnValues: 'UPDATED_NEW'). Reflect them
			// in currentUser so the streak badge updates immediately rather than
			// waiting for the next profile re-fetch.
			onFulfilled: (state, payload) => {
				const attributes = payload?.Attributes;
				if (state.currentUser && attributes) {
					state.currentUser = {
						...state.currentUser,
						loginStreakDays: attributes.loginStreakDays ?? state.currentUser.loginStreakDays,
						loginCount: attributes.loginCount ?? state.currentUser.loginCount
					};
				}
			}
		});
		buildReducers(builder, updateUserRoles, 'updateUserRequest');
		buildReducers(builder, inviteUser, 'createUserRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.organisation.dashboard.staff.successMessage'), { variant: 'success' });
			},
			onRejected: (state, action, detailedErrorFromAPI) => {
				if (detailedErrorFromAPI) {
					enqueueSnackbar(detailedErrorFromAPI, { variant: 'error' });
				}
				enqueueSnackbar(i18n.t('pages.organisation.dashboard.staff.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, createProspectiveUser, 'createProspectiveRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.organisation.dashboard.inviteProspective.successMessage'), {
					variant: 'success'
				});
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.organisation.dashboard.inviteProspective.errorMessage'), {
					variant: 'error'
				});
			}
		});
		buildReducers(builder, fetchUserStorage, 'walletStorageRequest', {
			onFulfilled: (state, payload) => {
				state.walletStorage = payload;
			}
		});
		buildReducers(builder, usersOrganisations, 'usersOrganisationsRequest', {
			onFulfilled: (state, payload) => {
				state.usersOrganisations = payload;
			}
		});
		buildReducers(builder, userGrants, 'usersGrantsRequest', {
			onFulfilled: (state, payload) => {
				state.userGrantsList = payload;
			}
		});
		buildReducers(builder, userLeavingOrganisation, 'userLeavingOrganisationRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('pages.account.leaveOrganisation.successMessage'), {
					variant: 'success'
				});
			}
		});
		buildReducers(builder, userOnboardingLicense, 'userOnboardingLicenseRequest', {
			onFulfilled: (state, payload) => {
				state.userOnboardingLicense = payload;
				state.isLoading = false;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state, error) => {
				enqueueSnackbar(error.message, { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, userNotifyTOTPLockedOut, 'totpLostRequest', {
			onFulfilled: (state) => {
				state.isLoading = false;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state, error) => {
				enqueueSnackbar(error.message, { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, getActiveSubscriptions, 'usersActiveSubscriptionsRequest', {
			onFulfilled: (state, payload) => {
				const { subscriptions, next, totalCount } = payload;
				state.usersActiveSubscriptions.items = subscriptions.map((subscription: any) => ({
					...subscription,
					id: subscription.orderId
				}));
				state.usersActiveSubscriptions.next = next;
				state.usersActiveSubscriptions.totalCount = totalCount;
			}
		});
		buildReducers(builder, cancelActiveSubscription, 'cancelActiveSubscriptionRequest', {
			onFulfilled: () => {
				enqueueSnackbar(i18n.t('common.types.user.cancelSubscriptionSuccessMessage'), {
					variant: 'success'
				});
			}
		});
		buildReducers(builder, readOrganisationUnlicensedUsers, 'usersUnlicensedStatusRequests', {
			onFulfilled: (state, payload) => {
				state.usersUnlicensedRequests = payload;
			}
		});
		buildReducers(builder, userFreeTrial, 'userFreeTrialRequest', {
			onFulfilled: (state, payload) => {
				state.userFreeTrial = payload;
			}
		});
		buildReducers(builder, userInfiniteFreeTier, 'userInfiniteFreeTierRequest', {
			onFulfilled: (state, payload) => {
				state.userInfiniteFreeTier = payload;
			}
		});
		buildReducers(builder, getUserGracePeriod, 'userGracePeriodRequest', {
			onFulfilled: (state, payload) => {
				state.userGracePeriod = payload;
			}
		});
		buildReducers(builder, stripeVerifyIdentity, 'stripeVerificationRequest', {
			onFulfilled: (state, payload) => {
				state.stripeVerification = payload;
			}
		});
		buildReducers(builder, readVerifyingUser, 'readVerifyingUserRequest', {
			onFulfilled: (state, payload) => {
				state.verifyingUser = payload;
			}
		});
	}
});

export const { resetUser, updateUserStripeIdentityVerified, updateUsersMFAStatus, resetUserConfirmedStatus } =
	userSlice.actions;
export default userSlice.reducer;
